import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CalendarAccount, CalendarEvent, CalendarEventInput, CalendarInfo, CalendarSource } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { addDays, dayKey, fromLocal, localInput, type DayKey } from '../lib/calendar';
import { t } from '../lib/i18n';
import { rangeText } from './Calendar';
import { Icon } from './Icon';
import { confirmDialog, Dialog } from './Overlay';
import { toast } from './Toasts';

/** Event detail card (opened from the calendar view) and the create / edit dialog. */

const LAST_CALENDAR = 'momomail.calendar.last';
const localZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export interface CalendarLookup {
  calendars: Map<string, CalendarSource>;
  accounts: Map<string, CalendarAccount>;
}

export function lookup(info: CalendarInfo | null): CalendarLookup {
  return {
    calendars: new Map((info?.calendars ?? []).map((c) => [c.id, c])),
    accounts: new Map((info?.accounts ?? []).map((a) => [a.id, a])),
  };
}

/** Text with http(s) links turned into buttons that open in the browser (nothing else is interpreted). */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s<>"']+)/g);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <button key={i} className="field__link cal-card__link" onClick={() => void api().mail.openExternal(p)}>
            {p}
          </button>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/** Floating card next to the clicked element; closes on outside click / Esc. */
export function FloatingCard({ anchor, onClose, children, className = '' }: { anchor: DOMRect; onClose: () => void; children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const gap = 8;
    let left = anchor.right + gap;
    if (left + w > window.innerWidth - 12) left = anchor.left - w - gap;
    if (left < 12) left = Math.min(Math.max(12, anchor.left), window.innerWidth - w - 12);
    let top = anchor.top;
    if (top + h > window.innerHeight - 12) top = window.innerHeight - h - 12;
    setPos({ left, top: Math.max(12, top) });
  }, [anchor]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as Element).closest('.dialog')) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.dialog')) {
        e.stopPropagation();
        onClose();
      }
    };
    // Defer so the click that opened the card doesn't close it
    const timer = setTimeout(() => window.addEventListener('mousedown', onDown), 0);
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);
  return (
    <div ref={ref} className={`cal-float ${className}`} style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden', left: 0, top: 0 }} role="dialog">
      {children}
    </div>
  );
}

export function EventCard({ event, look, onEdit, onDeleted, onClose }: { event: CalendarEvent; look: CalendarLookup; onEdit: () => void; onDeleted: () => void; onClose: () => void }) {
  const calendar = look.calendars.get(event.calendarId);
  const account = calendar ? look.accounts.get(calendar.accountId) : undefined;
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    const choice = await confirmDialog({ title: t('删除"{title}"？', { title: event.title || t('(无标题)') }), message: t('会从 {calendar} 里删掉，其他设备上也会消失。', { calendar: calendar?.name ?? t('日历') }), confirm: t('删除'), danger: true });
    if (choice !== 'confirm') return;
    setBusy(true);
    try {
      await api().calendar.deleteEvent(event.id);
      toast(t('已删除'), 'ok');
      onDeleted();
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cal-card" style={{ '--cal': calendar?.color ?? 'var(--violet)' } as React.CSSProperties}>
      <div className="cal-card__head">
        <span className="cal-card__swatch" aria-hidden />
        <h3>{event.title || t('(无标题)')}</h3>
        <button className="btn btn--icon btn--quiet" aria-label={t('关闭')} onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <p className="cal-card__row">
        <Icon name="clock" size={15} /> {rangeText(event.allDay, event.start, event.end)}
      </p>
      {calendar && (
        <p className="cal-card__row muted">
          <Icon name="calendar" size={15} /> {calendar.name}
          {account && account.name !== calendar.name ? ` · ${account.name}` : ''}
        </p>
      )}
      {event.location && (
        <p className="cal-card__row">
          <Icon name="pin" size={15} /> <Linkified text={event.location} />
        </p>
      )}
      {event.description && (
        <div className="cal-card__desc">
          <Linkified text={event.description} />
        </div>
      )}
      {(event.recurring || event.readOnly) && (
        <p className="cal-card__note muted">
          <Icon name="lock" size={13} /> {event.recurring ? t('这是重复的活动，暂时只能看。要修改请到原来的日历里改。') : t('这个日历是只读的。')}
        </p>
      )}
      <div className="cal-card__actions">
        {event.htmlLink && (
          <button className="btn btn--small btn--quiet" onClick={() => void api().mail.openExternal(event.htmlLink!)}>
            <Icon name="external" size={14} /> {t('在 Google 日历中打开')}
          </button>
        )}
        {!event.recurring && !event.readOnly && (
          <>
            <button className="btn btn--small btn--quiet tool--danger" disabled={busy} onClick={() => void remove()}>
              {busy ? <span className="spinner" /> : <Icon name="trash" size={14} />} {t('删除')}
            </button>
            <button className="btn btn--small btn--primary" disabled={busy} onClick={onEdit}>
              <Icon name="pen" size={14} /> {t('编辑')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** What the editor opens with: an existing event, or a new one on a day (optionally at a minute of that day). */
export type EditorRequest = { event: CalendarEvent } | { day: DayKey; minute?: number; allDay?: boolean };

interface Form {
  calendarId: string;
  title: string;
  allDay: boolean;
  startDate: DayKey;
  startTime: string;
  endDate: DayKey;
  endTime: string;
  location: string;
  description: string;
}

const hm = (minutes: number) => `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

function initialForm(req: EditorRequest, defaultCalendar: string): Form {
  if ('event' in req) {
    const e = req.event;
    if (e.allDay) return { calendarId: e.calendarId, title: e.title, allDay: true, startDate: e.start, startTime: '09:00', endDate: addDays(e.end, -1) < e.start ? e.start : addDays(e.end, -1), endTime: '10:00', location: e.location, description: e.description };
    const s = localInput(e.start);
    const en = localInput(e.end);
    return { calendarId: e.calendarId, title: e.title, allDay: false, startDate: s.date, startTime: s.time, endDate: en.date, endTime: en.time, location: e.location, description: e.description };
  }
  // New event: at the clicked slot, otherwise the next full hour (today) or 9:00
  const now = new Date();
  const minute = req.minute ?? (req.day === dayKey(now) ? Math.min(23 * 60, (now.getHours() + 1) * 60) : 9 * 60);
  const end = Math.min(minute + 60, 24 * 60 - 1);
  return { calendarId: defaultCalendar, title: '', allDay: !!req.allDay, startDate: req.day, startTime: hm(minute), endDate: req.day, endTime: hm(end), location: '', description: '' };
}

/** Writable calendars, default first: last used, else the first primary, else the first one. */
function pickDefault(calendars: CalendarSource[]): string {
  const writable = calendars.filter((c) => !c.readOnly);
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST_CALENDAR);
  } catch {
    /* ignore */
  }
  return (writable.find((c) => c.id === last) ?? writable.find((c) => c.primary && !c.hidden) ?? writable.find((c) => !c.hidden) ?? writable[0])?.id ?? '';
}

export function EventEditor({ request, info, onClose, onSaved }: { request: EditorRequest | null; info: CalendarInfo | null; onClose: () => void; onSaved: (e: CalendarEvent) => void }) {
  const calendars = useMemo(() => info?.calendars ?? [], [info]);
  const accounts = info?.accounts ?? [];
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (request) {
      setForm(initialForm(request, pickDefault(calendars)));
      setError(null);
    }
    // Only when a new request comes in (calendar list refreshes shouldn't reset the form)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const editing = request && 'event' in request ? request.event : null;
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const writable = calendars.filter((c) => !c.readOnly);

  let input: CalendarEventInput | null = null;
  let invalid: string | null = null;
  if (form) {
    const start = form.allDay ? form.startDate : fromLocal(form.startDate, form.startTime);
    const end = form.allDay ? addDays(form.endDate, 1) : fromLocal(form.endDate, form.endTime);
    if (!form.title.trim()) invalid = t('请填写标题');
    else if (!form.calendarId) invalid = t('先选一个日历');
    else if (form.allDay ? form.endDate < form.startDate : Date.parse(end) <= Date.parse(start)) invalid = t('结束时间要晚于开始时间');
    input = { calendarId: form.calendarId, title: form.title.trim(), allDay: form.allDay, start, end, location: form.location.trim(), description: form.description, timeZone: localZone() };
  }

  const save = async () => {
    if (!input || invalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const saved = editing ? await api().calendar.updateEvent(editing.id, input) : await api().calendar.createEvent(input);
      try {
        localStorage.setItem(LAST_CALENDAR, input.calendarId);
      } catch {
        /* ignore */
      }
      toast(editing ? t('已保存') : t('已添加到日历'), 'ok');
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  /** Moving the start keeps the length (end follows). */
  const moveStart = (date: DayKey, time: string) => {
    if (!form) return;
    if (form.allDay) {
      const days = Math.max(0, Math.round((+new Date(`${form.endDate}T00:00:00`) - +new Date(`${form.startDate}T00:00:00`)) / 86_400_000));
      set({ startDate: date, endDate: addDays(date, days) });
      return;
    }
    const length = Date.parse(fromLocal(form.endDate, form.endTime)) - Date.parse(fromLocal(form.startDate, form.startTime));
    const end = localInput(new Date(Date.parse(fromLocal(date, time)) + Math.max(15 * 60_000, length)).toISOString());
    set({ startDate: date, startTime: time, endDate: end.date, endTime: end.time });
  };

  const color = calendars.find((c) => c.id === form?.calendarId)?.color ?? 'var(--hair)';
  return (
    <Dialog
      open={!!request}
      onClose={onClose}
      title={editing ? t('编辑活动') : t('新建活动')}
      labelledBy="cal-editor-title"
      className="add-cal cal-editor"
      footer={
        form && (
          <>
            <button className="btn btn--quiet" onClick={onClose}>
              {t('取消')}
            </button>
            <button className="btn btn--primary" disabled={!!invalid || busy || !writable.length} onClick={() => void save()}>
              {busy ? <span className="spinner" /> : <Icon name="check" size={15} />} {t('保存')}
            </button>
          </>
        )
      }
    >
      {form && (
        <div
          className="add-cal__form"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey || (e.target as HTMLElement).tagName === 'INPUT')) {
              e.preventDefault();
              void save();
            }
          }}
        >
          {!writable.length && <div className="notice notice--warn">{t('还没有可以写入的日历。先在 设置 → 日历 里连接 Google 日历或 CalDAV 日历。')}</div>}
          <label className="field">
            <span className="field__label">{t('标题')}</span>
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} data-autofocus placeholder={t('比如：和小王吃饭')} />
          </label>
          <label className="add-cal__allday">
            <button type="button" className="switch" role="switch" aria-checked={form.allDay} aria-label={t('全天')} onClick={() => set({ allDay: !form.allDay })} /> {t('全天')}
          </label>
          <div className="cal-editor__times">
            <label className="field">
              <span className="field__label">{form.allDay ? t('开始日期') : t('开始')}</span>
              <span className="cal-editor__when">
                <input type="date" value={form.startDate} onChange={(e) => e.target.value && moveStart(e.target.value, form.startTime)} />
                {!form.allDay && <input type="time" step={900} value={form.startTime} onChange={(e) => e.target.value && moveStart(form.startDate, e.target.value)} />}
              </span>
            </label>
            <label className="field">
              <span className="field__label">{form.allDay ? t('结束日期') : t('结束')}</span>
              <span className="cal-editor__when">
                <input type="date" value={form.endDate} min={form.startDate} onChange={(e) => e.target.value && set({ endDate: e.target.value })} />
                {!form.allDay && <input type="time" step={900} value={form.endTime} onChange={(e) => e.target.value && set({ endTime: e.target.value })} />}
              </span>
            </label>
          </div>
          {input && !invalid && <p className="muted add-cal__when">{rangeText(input.allDay, input.start, input.end)}</p>}
          <label className="field">
            <span className="field__label">{t('日历')}</span>
            <span className="cal-editor__cal">
              <span className="cal-dot" style={{ background: color }} aria-hidden />
              <select value={form.calendarId} onChange={(e) => set({ calendarId: e.target.value })}>
                {!form.calendarId && <option value="">{t('选一个日历')}</option>}
                {accounts.map((a) => {
                  const own = writable.filter((c) => c.accountId === a.id);
                  return own.length ? (
                    <optgroup key={a.id} label={a.name}>
                      {own.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.hidden ? ` (${t('已隐藏')})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ) : null;
                })}
              </select>
            </span>
          </label>
          <label className="field">
            <span className="field__label">{t('地点')}</span>
            <input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder={t('可以不填')} />
          </label>
          <label className="field">
            <span className="field__label">{t('备注')}</span>
            <textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </label>
          {invalid && form.title.trim() && <p className="error-text">{invalid}</p>}
          {error && <div className="notice notice--error">{error}</div>}
          <p className="muted add-cal__when">{t('Ctrl+Enter 保存')}</p>
        </div>
      )}
    </Dialog>
  );
}
